import {
	ActionButton,
	Button,
	ProgressCircle,
	Switch,
	Text,
} from '@react-spectrum/s2';
import { style as spectrumStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { ArtifactStepper } from '@/components/artifact/ArtifactStepper';
import {
	formatMetricValue,
	normalizeWhere,
} from '@/spaces/campaigns/strategyFormat';
import {
	AudienceSection,
	CampaignsSection,
	MetricsSection,
} from '@/spaces/campaigns/StrategyPlanSections';
import { useLatestProgress } from '@/spaces/campaigns/useLatestProgress';
import { useStrategyDetail } from '@/spaces/campaigns/useStrategies';
import {
	type PlanEdit,
	useStrategyActions,
} from '@/spaces/campaigns/useStrategyActions';
import { useStrategyPlanView } from '@/spaces/campaigns/useStrategyPlanView';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import {
	ClampedProse,
	StepFieldEdit,
} from '@/spaces/chat/panels/StepFieldEdit';
import {
	buildStrategySteps,
	resolveStrategyStepIndex,
	strategyStepAdvanceLabel,
	strategyStepBackLabel,
	splitStatement,
	strategyStepKey,
	strategyStepLabel,
	type StrategyStep,
} from '@/spaces/chat/panels/strategySteps';
import { cn } from '@/utils/cn.ts';
import { trackEvent } from '@/utils/fullstory';

import type { StrategyChatPanel } from '../../../../../bridge/types/state.ts';

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
	<h3
		className="text-xs font-semibold tracking-widest text-(--text-disabled)
			uppercase"
	>
		{children}
	</h3>
);

// The daily worker works out a verdict, the next milestone and a projected date every cycle.
// Rendered as real text in the owner's words: `trajectory` is a machine enum and must never
// reach the screen raw. Each line renders only when its value is present, so a strategy with
// nothing measured yet says nothing rather than inventing certainty.
const TRAJECTORY_LABEL: Record<string, string> = {
	ahead: 'Ahead of plan',
	on_track: 'On track',
	behind: 'Behind plan',
	insufficient_data: 'Not enough data yet',
};

const ProgressVerdict = ({
	progress,
}: {
	progress?: {
		trajectory?: string | null;
		next_checkpoint?: number | null;
		projected_arrival?: string | null;
	};
}) => {
	if (!progress) return null;
	const verdict = progress.trajectory
		? (TRAJECTORY_LABEL[progress.trajectory] ?? null)
		: null;
	const arrival = progress.projected_arrival
		? new Date(progress.projected_arrival).toLocaleDateString(undefined, {
				month: 'long',
				day: 'numeric',
			})
		: null;
	if (!verdict && !arrival && progress.next_checkpoint == null) return null;

	return (
		<div
			data-testid="strategy-progress-verdict"
			className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm
				text-(--text-muted)"
		>
			{verdict && (
				<span
					data-testid="strategy-progress-trajectory"
					className="font-semibold text-(--text-primary)"
				>
					{verdict}
				</span>
			)}
			{progress.next_checkpoint != null && (
				<span data-testid="strategy-progress-next">
					Next milestone {progress.next_checkpoint.toLocaleString()}
				</span>
			)}
			{arrival && (
				<span data-testid="strategy-progress-eta">
					On track to reach it by {arrival}
				</span>
			)}
		</div>
	);
};

// A thread opened FROM a flag or a proposal is about a strategy that is already running. It asks
// one question about the plan, so it needs the whole plan on screen to answer it. The guided walk
// is for the owner deciding whether to start a plan at all. Written as a negative test on purpose:
// a strategy panel in a thread with no linked item must still get the walk.
const REFERENCE_THREAD_TYPES = new Set(['strategy_flag', 'strategy_proposal']);

// How long Activate ignores a press after the walk lands on the recap. See `handleActivate`: the
// advance control and Activate are the same button node, so the second press of a double-press on
// the last campaign used to start the strategy. Wide enough to cover a browser double-click, which
// is under half a second, and far short of reading a recap.
const RECAP_SETTLE_MS = 600;

export const StrategyReviewPanel = ({
	panel,
	onBack,
	onClose,
	style,
}: {
	panel: StrategyChatPanel;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}) => {
	const strategyId = panel.item_id;
	const bridgeApi = useBridgeApi();
	const { run, loading, notFound, error, refresh } =
		useStrategyDetail(strategyId);
	const { activate, deactivate, materializeCampaign, editPlan } =
		useStrategyActions();

	// What the SAVE changed besides the field the owner typed in. The route answers with one
	// sentence per op it applied, in the order it applied them: the owner's own edits first, then
	// the cascade's, then any campaign it invalidated. So everything past the ops we sent is a
	// consequence the owner did not ask for by hand, and it is the part they have to be told.
	// Four call sites used to discard this list, so the statement and the metric names changed
	// under the owner in silence.
	//
	// Both of these carry the part the save STARTED on, and both are compared against the live
	// part at RENDER time. The route answers after the press and nothing stops the owner
	// advancing meanwhile, so the answer has to say which part it belongs to. Deciding that when
	// the answer lands would race the same way the bug did: the step arrives on a bridge delta,
	// and a rejected fetch is a microtask, so neither one reliably goes first.
	const [cascade, setCascade] = useState<{
		step: number;
		notes: string[];
	} | null>(null);
	// A save the owner walked away from. `StepFieldEdit` reports its own failure beside the value,
	// but it unmounts with the part, so an edit that failed after an advance was lost in silence:
	// no message anywhere, and the recap they activated still carried the old wording. This lives
	// on the panel, outside the swapping step body, so it survives that unmount.
	// `inField` says the field is still showing this same failure beside the value. It is true
	// only until the owner leaves the part, because the step body is keyed by the step: walking
	// away unmounts `StepFieldEdit`, and walking back remounts it with an empty error.
	const [lostSave, setLostSave] = useState<{
		step: number;
		message: string;
		inField: boolean;
	} | null>(null);
	const progress = useLatestProgress();
	const view = useStrategyPlanView(run);

	const [activating, setActivating] = useState(false);
	// When the walk ARRIVED at the recap, or null when it did not arrive there under this panel.
	// Read by `handleActivate`; written by the effect beside `isLastStep`, which is the first
	// place the value exists.
	const recapShownAt = useRef<number | null>(null);
	const wasOnRecap = useRef<boolean | null>(null);
	// Counts every move between parts. `applyPlanEdits` reads it before the await and again when
	// the save rejects: an unchanged count is the only proof the owner never left, and so the only
	// proof the field that started the save is still mounted to report the failure itself.
	// Comparing the step instead missed a walk OUT and BACK, which lands on the same number.
	const stepMoves = useRef(0);
	const [actionError, setActionError] = useState<string | null>(null);
	const [pausePending, setPausePending] = useState(false);
	useEffect(() => {
		setPausePending(false);
	}, [run?.status, run?.updated_at]);

	useEffect(() => {
		if (panel.revision === undefined) return;
		void refresh();
	}, [panel.revision, refresh]);

	const handleBuildCampaign = useCallback(
		async (sid: string, cid: string) => {
			const reply = await materializeCampaign(sid, cid);
			void refresh();
			return reply;
		},
		[materializeCampaign, refresh],
	);

	const handleViewSetup = useCallback(
		(processId: string) => {
			trackEvent('Lane2 Viewed', {
				process_id: processId,
				from: 'review_panel',
			});
			bridgeApi.sendAction({
				type: 'do_open_chat_panel',
				panel_type: 'artifact',
				artifact_type: 'campaign',
				item_id: processId,
			});
		},
		[bridgeApi],
	);

	const handleActivate = useCallback(async () => {
		if (activating) return;
		// The advance control and Activate are ONE button node. They sit at the same position in
		// the same fragment, each wrapped in a <span>, so React updates that node in place rather
		// than replacing it. On the last-but-one part the first press of a double-press advances
		// the walk, the bridge delta relabels that same node "Activate", and the second press
		// starts the strategy — which begins the daily loop and publishes campaign work while the
		// owner has not yet read a word of the recap.
		//
		// Guarded by TIME, not by remounting the button. A key would replace the node, and a real
		// double-click's second press lands on whatever sits under the cursor, so the fresh
		// Activate button would take it just the same.
		//
		// A press this soon after the recap appeared is the tail of that double-press, not a
		// decision about the recap. A browser double-click is under half a second by definition,
		// and `do_panel_step_next_reducer` is a pure synchronous state transform with no server
		// IO, so the whole window closes inside one.
		const shown = recapShownAt.current;
		if (shown !== null && Date.now() - shown < RECAP_SETTLE_MS) return;
		trackEvent('Strategy Activated', {
			strategy_id: strategyId,
			from: 'review_panel',
		});
		setActivating(true);
		setActionError(null);
		try {
			await activate(strategyId);
			await refresh();
		} catch (err) {
			setActionError(
				err instanceof Error
					? err.message
					: "We couldn't start this strategy. Please try again.",
			);
			if ((err as { status?: number })?.status === 409) await refresh();
		} finally {
			setActivating(false);
		}
	}, [activating, activate, refresh, strategyId]);

	const handlePause = useCallback(async () => {
		setPausePending(true);
		setActionError(null);
		try {
			await deactivate(strategyId);
			await refresh();
		} catch (err) {
			setPausePending(false);
			setActionError(
				err instanceof Error
					? err.message
					: "We couldn't stop this strategy. Please try again.",
			);
			if ((err as { status?: number })?.status === 409) await refresh();
		}
	}, [deactivate, refresh, strategyId]);

	const status = run?.status;
	const isActive = status === 'active';
	const hasPlan = status === 'complete' || status === 'active';

	const threadType =
		bridgeApi.user_state?.chats?.[panel.chat_id]?.linked_item?.type;
	const steps = useMemo(() => buildStrategySteps(view), [view]);
	// The walk is for the pre-activation decision only. A running strategy and a flag or proposal
	// thread keep the full reference view.
	//
	// A STOPPED strategy keeps it too, and `status` alone cannot find one: deactivating returns
	// the run to "complete", so a strategy the owner ran and stopped reads exactly like one they
	// never started. `deactivated_at` is the signal that separates them, which is the same test
	// `strategyGroupOf` uses to tell "paused" from "new". Without it, pressing Stop collapsed the
	// whole plan into the pre-activation walk at whatever step the cursor last held.
	const guided =
		status === 'complete' &&
		!run?.deactivated_at &&
		!REFERENCE_THREAD_TYPES.has(threadType ?? '');

	const rawStep = panel.step_index ?? 0;
	const stepIndex = Math.min(
		Math.max(rawStep, 0),
		Math.max(steps.length - 1, 0),
	);

	// Defined HERE, below the cursor, so it closes over the part that was on screen when the owner
	// pressed Save. That is the whole tag: no ref, and nothing read after the await.
	const applyPlanEdits = useCallback(
		async (ops: Parameters<typeof editPlan>[1]) => {
			const movesAtStart = stepMoves.current;
			try {
				const applied = await editPlan(strategyId, ops);
				setCascade({ step: stepIndex, notes: applied.slice(ops.length) });
				// This save answers the one that was lost on the SAME part, so the notice goes.
				// Nothing else clears it, and the panel is keyed by the strategy rather than by the
				// step, so one failure followed the owner onto every other part for the life of the
				// panel — including after they came back and saved the very same edit. Scoped by
				// step on purpose: another part's edit can still be lost, and it keeps its notice.
				setLostSave((prev) => (prev && prev.step === stepIndex ? null : prev));
				await refresh();
			} catch (err) {
				setLostSave({
					step: stepIndex,
					message:
						err instanceof Error
							? err.message
							: "We couldn't save that change. Please try again.",
					// The field can only be showing this if the owner never left the part while the
					// save was in flight. One move is enough to unmount it, and walking out and
					// back returns to the same step index, so the COUNT is what has to be equal.
					inField: stepMoves.current === movesAtStart,
				});
				// Rethrown so `StepFieldEdit` still shows it in place and keeps the owner's draft,
				// for the ordinary case where they never left the part.
				throw err;
			}
		},
		[editPlan, refresh, stepIndex, strategyId],
	);

	// The notice describes a save, not a part, so it goes when the owner moves on. Left in place
	// it would follow them to a part it says nothing about.
	const cascadeNotes = cascade?.step === stepIndex ? cascade.notes : [];
	// Shown only once the field that would have reported it is gone. While the owner is still on
	// that part `StepFieldEdit` has it, and two copies of one failure read as two failures.
	//
	// Keyed on `inField` rather than on the step. Comparing the step suppressed the notice on
	// EVERY visit to the failing part, including a return trip — and by then the field had
	// remounted empty, so the owner came back to fix the lost save and saw nothing at all.
	const lostSaveError = lostSave && !lostSave.inField ? lostSave.message : null;
	const step: StrategyStep | undefined = steps[stepIndex];
	const isLastStep = stepIndex >= steps.length - 1;

	useEffect(() => {
		stepMoves.current += 1;
		// The owner moved, so the field that carried the failure has unmounted. It cannot carry it
		// again — a remount starts with no error — so from here the panel is the only surface left
		// that can report it. One way only: nothing sets `inField` back to true.
		setLostSave((prev) =>
			prev && prev.inField ? { ...prev, inField: false } : prev,
		);
	}, [stepIndex]);

	useEffect(() => {
		// The first run is the mount. A panel that opens straight onto the recap — a reload, or a
		// thread that was already there — has no advance press behind it, so there is nothing to
		// guard and Activate answers at once. Cleared on the way out as well as set on the way in,
		// so walking Back and forward again re-arms it rather than leaving a stale stamp.
		recapShownAt.current =
			wasOnRecap.current !== null && isLastStep && !wasOnRecap.current
				? Date.now()
				: null;
		wasOnRecap.current = isLastStep;
	}, [isLastStep]);

	const sendStep = useCallback(
		(next: number) => {
			bridgeApi.sendAction({ type: 'do_panel_step_next', step_index: next });
		},
		[bridgeApi],
	);

	// Keep the bridge cursor and the rendered part in agreement when the PLAN changes underneath the
	// owner. It must never touch a deliberate advance. Those two look identical from a single render
	// — the index moved — so the effect keys off the step LIST's identity, not off the index.
	//
	// Correcting on every render instead undoes every press: the owner advances to part 2, the
	// effect re-finds the part-1 key still in the unchanged list, and sends the cursor back to 1.
	// The counter then parks on part 1 for ever. Caught by ado-1984-guided-strategy-setup.spec.ts,
	// and invisible to a component test that renders at a fixed step without dispatching.
	const listIdRef = useRef<string | null>(null);
	const lastKeyRef = useRef<string | null>(null);
	const lastSentRef = useRef<string | null>(null);
	const listId = useMemo(() => steps.map(strategyStepKey).join('|'), [steps]);
	useEffect(() => {
		if (!guided) return;
		// Never correct against a plan this panel is not showing. Without this the owner can open
		// strategy B while strategy A's run is still in the hook, and the correction writes B's
		// cursor from A's step list.
		if (!run || run.id !== panel.item_id) return;

		const previousList = listIdRef.current;
		const listChanged = previousList !== null && previousList !== listId;
		listIdRef.current = listId;

		// The plan is the same one the owner has been reading. Their index is theirs. Record where
		// they are so a LATER plan change can carry them, and dispatch nothing — unless the cursor
		// sits outside the list, which a shortened plan or a stale persisted value can both cause.
		if (!listChanged) {
			const inRange = rawStep >= 0 && rawStep <= steps.length - 1;
			if (inRange) {
				lastKeyRef.current = steps[rawStep]
					? strategyStepKey(steps[rawStep])
					: null;
				return;
			}
		}

		const resolved = resolveStrategyStepIndex(
			steps,
			listChanged ? lastKeyRef.current : null,
			rawStep,
		);
		lastKeyRef.current = steps[resolved]
			? strategyStepKey(steps[resolved])
			: null;
		if (resolved === rawStep) return;
		// One dispatch per (panel, list shape, destination). The delta returns a round trip later,
		// and every render inside that window would otherwise re-evaluate the same true condition.
		const latch = `${panel.item_id}:${listId}:${resolved}`;
		if (lastSentRef.current === latch) return;
		lastSentRef.current = latch;
		sendStep(resolved);
	}, [guided, run, panel.item_id, steps, listId, rawStep, sendStep]);

	const headerTagRender = isActive
		? () => {
				const showAsActive = !pausePending;
				return (
					<Switch
						isSelected={showAsActive}
						onChange={() => void handlePause()}
						isDisabled={pausePending}
						isEmphasized
						size="M"
						aria-label="Stop this strategy"
					>
						{showAsActive ? 'Active' : 'Stopping…'}
					</Switch>
				);
			}
		: undefined;

	const activateControl = () => (
		// Named because a campaign row inside this same panel also offers "Activate",
		// so matching by label alone reaches the wrong gate. The name sits on a
		// wrapper because an S2 Button drops unknown DOM props.
		<span data-testid="strategy-panel-activate">
			<Button
				variant="accent"
				isPending={activating}
				isDisabled={activating}
				onPress={() => void handleActivate()}
			>
				<Text>Activate</Text>
			</Button>
		</span>
	);

	function guidedControls(): React.ReactNode {
		if (!step) return null;
		return (
			<>
				{stepIndex > 0 && steps[stepIndex - 1] && (
					<span data-testid="strategy-step-back">
						<ActionButton
							isQuiet
							aria-label={strategyStepBackLabel(steps[stepIndex - 1])}
							onPress={() =>
								bridgeApi.sendAction({ type: 'do_panel_step_back' })
							}
						>
							<Text>Back</Text>
						</ActionButton>
					</span>
				)}
				{isLastStep ? (
					activateControl()
				) : (
					<span data-testid="strategy-step-next">
						<Button variant="accent" onPress={() => sendStep(stepIndex + 1)}>
							<Text>{strategyStepAdvanceLabel(step)}</Text>
						</Button>
					</span>
				)}
			</>
		);
	}

	const actionRenderer =
		status !== 'complete'
			? undefined
			: guided
				? guidedControls
				: activateControl;

	const quiet = (message: string) => (
		<p className="text-sm text-(--text-muted)">{message}</p>
	);

	const primaryMetric = view.orderedMetrics.find(
		(m) => m.variant === 'primary',
	)?.metric;

	const ideaBody = () => {
		// `statement` is generated under validator rule R23, which welds two ideas into one 400-to-
		// 500-character string. Rendered whole it IS the wall this walk exists to remove, so it is
		// split on the pivot R23 guarantees and each half is clamped. Rendered as PLAIN text:
		// `glossSites` is built from a BLANKED statement, so glossing here would place definition
		// chips at the wrong site.
		//
		// The header already shows `wish.outcome`, so this body must never echo it.
		const halves = splitStatement(view.strategy?.statement);
		if (!halves) return quiet('There is no plan to review yet.');

		// The planner writes the diagnosis, and it arrives untyped from the run document. A
		// number renders as a stray digit, and an object throws out of React — the only
		// boundary is at the application root, so that costs the owner the whole screen.
		const diagnosis =
			typeof view.strategy?.bottleneckDiagnosis === 'string'
				? view.strategy.bottleneckDiagnosis.trim()
				: '';

		return (
			<div className="space-y-5">
				{/* Authored copy, never data, so it cannot blank. It answers "why can't I change
				    anything" before the owner has to ask it. */}
				<p className="text-sm text-(--text-secondary)">
					Here is the goal I would start from. Change anything that is wrong.
				</p>

				{/* Both halves came from ONE sentence, so they sit in one card. Two cards would
				    imply two separate facts, and the metric card next to it is the only other one. */}
				{halves && (
					<div className="glass-card-sm space-y-3 p-4">
						{/* The GOAL is what `set_wish` edits, so that is what the control offers. The
						    statement itself has no edit op, and offering one would be a lie. */}
						<StepFieldEdit
							kind="text"
							label="The plan"
							testId="strategy-step-wish"
							openLabel="Change the goal"
							value={view.strategy?.wish?.outcome ?? ''}
							onSave={async (next) => {
								await applyPlanEdits([{ op: 'set_wish', outcome: next }]);
							}}
						>
							<ClampedProse
								text={halves.plan}
								lines={3}
								testId="strategy-step-plan"
								className="text-base leading-relaxed text-(--text-primary)"
							/>
						</StepFieldEdit>

						{/* The diagnosis reads BEFORE the argument, and inside the same card, because
						    it is why this goal was chosen. Its own card would read as a detached
						    second opinion — and if it names a blocker the owner did not pick, that
						    costs the trust the line exists to earn.

						    "What's holding you back" is the interview's own wording. The owner is
						    never shown the word "bottleneck". */}
						{diagnosis && (
							<div className="space-y-1 border-t border-black/[0.06] pt-3">
								<p
									className="text-xs font-semibold tracking-wide
										text-(--text-muted) uppercase"
								>
									What&apos;s holding you back
								</p>
								<ClampedProse
									text={diagnosis}
									lines={3}
									testId="strategy-step-bottleneck"
									className="text-sm leading-relaxed text-(--text-secondary)"
								/>
							</div>
						)}

						{halves.notThis && (
							<div className="space-y-1 border-t border-black/[0.06] pt-3">
								{/* "Why this wins", NOT "Not this". The half opens with "We will win
								    because", and its contrast is one closing clause — about 15 percent
								    of the text. A label that describes a seventh of its own content is
								    simply wrong. */}
								<p
									className="text-xs font-semibold tracking-wide
										text-(--text-muted) uppercase"
								>
									Why this wins
								</p>
								<ClampedProse
									text={halves.notThis}
									lines={3}
									testId="strategy-step-notthis"
									className="text-sm leading-relaxed text-(--text-secondary)"
								/>
							</div>
						)}
					</div>
				)}
			</div>
		);
	};

	// Success is its own part now. A plan and the number it is judged on are two decisions, and
	// pairing them made both harder to read. Everything here is editable through ONE op, because an
	// owner who disagrees with the number usually disagrees with how it is counted too.
	const successBody = () => {
		if (!primaryMetric) return quiet('This plan has nothing to measure yet.');
		const m = primaryMetric.metric;
		// The route resolves a metric by its id OR by its name, and takes the FIRST metric when the
		// selector is empty (`if not wanted or wanted in names`). An id is optional on the wire and
		// `normalizePlanMetric` coerces an absent one to "", so an empty selector is a real state —
		// and the primary need not be first, because `useStrategyPlanView` moves it to the front of
		// its OWN list only. So the panel edited the primary and the route wrote a supporting
		// metric, silently. The name is the only other selector the route reads (ADO-1984).
		const metricSelector = (primaryMetric.id || m.name || '').trim();
		// Typed to the op's own fields, not `Record<string, unknown>`. A loose record spreads past
		// the PlanEdit union and lets a misspelled field reach the route, which answers 422.
		const saveMetric = async (
			edit: Partial<
				Pick<
					Extract<PlanEdit, { op: 'set_metric' }>,
					'name' | 'purpose' | 'target' | 'unit' | 'baseline'
				>
			>,
		) => {
			await applyPlanEdits([
				{
					op: 'set_metric',
					...(metricSelector ? { metric: metricSelector } : {}),
					...edit,
				},
			]);
		};
		return (
			<div className="space-y-5">
				<p className="text-sm text-(--text-secondary)">
					This is the one number the plan is judged on. Change any part of it
					and I will rewrite the rest to match.
				</p>

				<div className="glass-card-sm space-y-3 p-4">
					<StepFieldEdit
						kind="number"
						label="The number to reach"
						testId="strategy-step-target"
						openLabel="Change the number"
						value={m.target ?? null}
						onSave={(next) => saveMetric({ target: next })}
					>
						<p
							data-testid="strategy-step-metric"
							className="text-lg font-semibold text-(--text-primary)"
						>
							{formatMetricValue(m.target, m.unit)}
						</p>
					</StepFieldEdit>

					<div className="border-t border-black/[0.06] pt-3">
						<StepFieldEdit
							kind="text"
							label="What gets counted"
							testId="strategy-step-metric-name"
							openLabel="Change what gets counted"
							value={m.name ?? ''}
							onSave={(next) => saveMetric({ name: next })}
						>
							<p className="text-sm text-(--text-primary)">{m.name}</p>
						</StepFieldEdit>
					</div>

					<div className="border-t border-black/[0.06] pt-3">
						<StepFieldEdit
							kind="text"
							label="Counted in"
							testId="strategy-step-metric-unit"
							openLabel="Change the unit"
							value={m.unit ?? ''}
							onSave={(next) => saveMetric({ unit: next })}
						>
							<p className="text-sm text-(--text-primary)">
								{m.unit || 'not set'}
							</p>
						</StepFieldEdit>
					</div>

					<div className="border-t border-black/[0.06] pt-3">
						<StepFieldEdit
							kind="number"
							label="Where it stands today"
							testId="strategy-step-metric-baseline"
							openLabel="Change today's figure"
							// Zero, not one. The route already accepts it — it refuses only a
							// NEGATIVE baseline (`strategies.py`) — and starting from nothing is
							// the commonest honest answer on a plan that has not run yet. The
							// field inherited the TARGET's floor, and a NumberField CLAMPS to its
							// floor rather than refusing, so a typed 0 was saved as 1: a starting
							// point the owner never gave.
							minValue={0}
							value={m.baseline ?? null}
							onSave={(next) => saveMetric({ baseline: next })}
						>
							<p className="text-sm text-(--text-primary)">
								{m.baseline == null
									? 'not set'
									: formatMetricValue(m.baseline, m.unit)}
							</p>
						</StepFieldEdit>
					</div>

					<div className="border-t border-black/[0.06] pt-3">
						<StepFieldEdit
							kind="text"
							label="Why this number"
							testId="strategy-step-metric-purpose"
							openLabel="Change why this number"
							value={primaryMetric.purpose ?? ''}
							onSave={(next) => saveMetric({ purpose: next })}
						>
							<p className="text-sm text-(--text-secondary)">
								{primaryMetric.purpose?.trim() || 'Not said yet.'}
							</p>
						</StepFieldEdit>
					</div>

					{/* Where the number came from. The route drops this the moment the owner
					    retargets, because the evidence backed the OLD number. */}
					{m.basis?.quote && (
						<p
							className="border-t border-black/[0.06] pt-3 text-xs
								text-(--text-muted)"
						>
							From your own research: &ldquo;{m.basis.quote}&rdquo;
						</p>
					)}
				</div>
			</div>
		);
	};

	const audienceBody = () => {
		// Where they gather is the half of the audience the owner can ACT on: it names the
		// subreddits, the events and the accounts a campaign has to reach. Leaving it out reduced
		// this part to one sentence the owner had already read in the statement.
		const where = normalizeWhere(view.strategy?.audience?.where);
		return (
			<div className="space-y-4">
				<p className="text-sm text-(--text-secondary)">
					This is who the plan is written for. Change it and I will rewrite the
					rest to match.
				</p>
				<div className="glass-card-sm p-4">
					<StepFieldEdit
						kind="text"
						label="Who this is for"
						testId="strategy-step-audience"
						openLabel="Change who this is for"
						value={view.strategy?.audience?.who ?? ''}
						onSave={async (next) => {
							await applyPlanEdits([{ op: 'set_audience_who', who: next }]);
						}}
					>
						<p className="text-sm leading-relaxed text-(--text-primary)">
							{view.strategy?.audience?.who}
						</p>
					</StepFieldEdit>
				</div>
				{where.length > 0 && (
					<div
						className="glass-card-sm space-y-1 p-4"
						data-testid="strategy-step-audience-where"
					>
						<p
							className="text-xs font-semibold tracking-wide text-(--text-muted)
								uppercase"
						>
							Where they gather
						</p>
						<ul className="divide-y divide-(--glass-border)">
							{where.map((locus, i) => (
								<li key={i} className="space-y-0.5 py-3 first:pt-1 last:pb-0">
									<p
										className="text-sm leading-snug font-semibold
											text-(--text-primary)"
									>
										{locus.place}
									</p>
									{locus.note && (
										<p className="text-sm leading-relaxed text-(--text-muted)">
											{locus.note}
										</p>
									)}
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		);
	};

	const campaignBody = (campaignId: string) => {
		// Delegates to the shared card on purpose. That card owns the build control, the build
		// status, the failure copy and the built work — guarantees this walk must not lose. Its
		// READABILITY was the fault, and that is fixed inside the card itself, where the dock gets
		// it too.
		return (
			<div className="space-y-3">
				<p className="text-sm text-(--text-secondary)">
					This is one of the moves that gets you there. Change anything that is
					not right.
				</p>

				{run && (
					<CampaignsSection
						run={run}
						view={view}
						showProv={false}
						campaignIds={[campaignId]}
						onEditCampaign={async (cid, edit) => {
							await applyPlanEdits([
								{ op: 'set_campaign', campaign_id: cid, ...edit },
							]);
						}}
						campaignActivation={{
							strategyId,
							onActivate: handleBuildCampaign,
							onViewSetup: handleViewSetup,
						}}
					/>
				)}
			</div>
		);
	};

	const recapBody = () => (
		// Lines only. A recap that rebuilt the campaign cards would put the wall back one press
		// before the owner decides, which is the whole fault this panel is fixing.
		<div className="space-y-4">
			<p className="text-sm text-(--text-muted)">
				Here is what starts when you activate.
			</p>
			<ul className="divide-y divide-(--glass-border)">
				{view.strategy?.wish?.outcome && (
					<li className="py-2 text-sm text-(--text-primary)">
						{view.strategy.wish.outcome}
					</li>
				)}
				{primaryMetric && (
					<li className="py-2 text-sm text-(--text-primary)">
						{formatMetricValue(
							primaryMetric.metric.target,
							primaryMetric.metric.unit,
						)}{' '}
						<span className="text-(--text-muted)">
							{primaryMetric.metric.name}
						</span>
					</li>
				)}
				{view.strategy?.audience?.who && (
					<li className="py-2 text-sm text-(--text-primary)">
						{view.strategy.audience.who}
					</li>
				)}
				{view.campaigns.map((c) => (
					<li
						key={c.id}
						data-testid="strategy-recap-campaign"
						className="py-2 text-sm text-(--text-primary)"
					>
						{c.title}
					</li>
				))}
			</ul>
		</div>
	);

	const stepBody = (current: StrategyStep) => {
		switch (current.kind) {
			case 'idea':
				return ideaBody();
			case 'success':
				return successBody();
			case 'audience':
				return audienceBody();
			case 'campaign':
				return campaignBody(current.campaignId);
			case 'recap':
				return recapBody();
		}
	};

	const guidedBody = () => {
		if (!step) return quiet('There is no plan to review on this strategy yet.');
		return (
			<div className="flex min-h-0 flex-col">
				<ArtifactStepper
					labels={steps.map(strategyStepLabel)}
					currentIndex={stepIndex}
				/>
				{cascadeNotes.length > 0 && (
					// What the save changed that the owner did not type. Not an error, so it does
					// not take the error colour, and it sits ABOVE the part because it describes
					// the plan as a whole rather than the field being read.
					<div
						data-testid="strategy-cascade-notes"
						className="mb-3 space-y-1 rounded-md border border-black/[0.06] px-3
							py-2"
					>
						<p
							className="text-xs font-semibold tracking-wide text-(--text-muted)
								uppercase"
						>
							Also updated to match
						</p>
						{cascadeNotes.map((note) => (
							<p
								key={note}
								className="text-sm leading-relaxed text-(--text-secondary)"
							>
								{note}
							</p>
						))}
					</div>
				)}
				<div
					// Keyed by the step so the enter animation replays on every advance. The
					// utility already carries its own prefers-reduced-motion fallback.
					key={strategyStepKey(step)}
					data-testid="strategy-guided-step"
					data-step-kind={step.kind}
					tabIndex={-1}
					className={cn('animate-grounding-in-up min-h-0 pb-2')}
				>
					{stepBody(step)}
				</div>
			</div>
		);
	};

	const fullBody = () => (
		<div className="space-y-6 pb-2">
			<div className="space-y-3">
				<SectionHeading>Campaigns</SectionHeading>
				<CampaignsSection
					run={run!}
					view={view}
					showProv={false}
					campaignActivation={{
						strategyId,
						onActivate: handleBuildCampaign,
						onViewSetup: handleViewSetup,
					}}
				/>
			</div>
			<div className="space-y-3">
				<SectionHeading>Audience</SectionHeading>
				<AudienceSection view={view} showProv={false} />
			</div>
			<div className="space-y-3">
				<SectionHeading>How we&apos;ll measure it</SectionHeading>
				<ProgressVerdict progress={progress.byStrategyId[strategyId]} />
				<MetricsSection
					run={run!}
					view={view}
					showProv={false}
					currentValue={progress.byStrategyId[strategyId]?.current_value}
				/>
			</div>
		</div>
	);

	const body = () => {
		if (notFound) return quiet('This strategy is no longer available.');
		if (error) return quiet(error);
		if (!run && loading)
			return (
				<div className="flex items-center gap-2">
					<ProgressCircle
						isIndeterminate
						size="S"
						staticColor="auto"
						aria-label="Loading strategy"
					/>
					<Text
						styles={spectrumStyle({
							font: 'body',
							fontSize: 'body-sm',
							color: 'detail',
						})}
					>
						Loading strategy…
					</Text>
				</div>
			);
		if (!run) return quiet('This strategy is no longer available.');
		if (status === 'generating')
			return (
				<div className="flex items-center gap-2">
					<ProgressCircle
						isIndeterminate
						size="S"
						staticColor="auto"
						aria-label="Writing strategy"
					/>
					{quiet('This strategy is still being written.')}
				</div>
			);
		if (status === 'failed')
			return quiet(
				run.error?.trim() ||
					"This strategy didn't finish generating, so there's nothing to review yet.",
			);
		if (!hasPlan || !view.strategy)
			return quiet('There is no plan to review on this strategy yet.');

		return guided ? guidedBody() : fullBody();
	};

	return (
		<ChatPanelLayout
			title={view.strategy?.wish?.outcome?.trim() || 'Strategy'}
			// The walk puts one part on screen at a time, and the header description would keep a
			// clamped copy of the statement above every one of them. Step 0 renders it in full.
			description={
				guided ? undefined : view.strategy?.statement?.trim() || undefined
			}
			headerTagRender={headerTagRender}
			actionRenderer={actionRenderer}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			<div data-testid="strategy-review-panel">
				{body()}
				{/* Announced on every advance. Lives in the wrapper, OUTSIDE the swapping step
				    body, because a live region inside the swapped node is destroyed before it
				    announces. */}
				{guided && step && (
					<span
						data-testid="strategy-step-progress"
						aria-live="polite"
						role="status"
						className="sr-only"
					>
						{`Step ${stepIndex + 1} of ${steps.length}: ${strategyStepLabel(step)}`}
					</span>
				)}
				{actionError && (
					<p
						data-testid="strategy-panel-activate-error"
						className="mt-3 text-sm text-red-600"
					>
						{actionError}
					</p>
				)}
				{/* Outside `body()` on purpose, like the live region above it: the part the owner
				    was editing has already unmounted by the time this is set. */}
				{lostSaveError && (
					<p
						data-testid="strategy-panel-lost-save-error"
						className="mt-3 text-sm text-red-600"
					>
						{lostSaveError}
					</p>
				)}
			</div>
		</ChatPanelLayout>
	);
};
